/** Design-system primitives for the S&P monochrome navy theme. Small on purpose:
 *  everything here maps to a token in index.css. */

import { clsx } from 'clsx'
import { Loader2, X } from 'lucide-react'
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRight } from 'lucide-react'
import mark from '../../assets/sp-mark.png'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md' | 'lg'

export function Button({
  variant = 'secondary', size = 'md', loading, className, children, icon, ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size; loading?: boolean; icon?: ReactNode }) {
  const base = 'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors duration-[140ms] focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-700 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap'
  const sizes = { sm: 'h-8 px-3 text-[13px]', md: 'h-9.5 px-4 text-sm', lg: 'h-11 px-5 text-[15px]' }
  const variants = {
    primary: 'bg-navy-800 text-white hover:bg-navy-900 active:shadow-[inset_0_1px_2px_rgba(0,0,0,0.2)]',
    secondary: 'bg-white text-navy-800 border border-navy-300 hover:bg-navy-50 hover:border-navy-700',
    ghost: 'text-navy-800 hover:bg-navy-50',
    danger: 'bg-danger-500 text-white hover:bg-danger-700',
  }
  return (
    <button className={clsx(base, sizes[size], variants[variant], className)} disabled={loading || rest.disabled} {...rest}>
      {loading ? <Loader2 className="size-4 animate-spin" /> : icon}
      {children}
    </button>
  )
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={clsx('input', props.className)} />
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={clsx('input min-h-24 leading-relaxed', props.className)} />
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={clsx('input pr-8', props.className)} />
}

export function Field({ label, hint, error, children, className }: { label: string; hint?: string; error?: string; children: ReactNode; className?: string }) {
  return (
    <label className={clsx('block', className)}>
      <span className="label">{label}</span>
      {children}
      {hint && !error && <span className="mt-1 block text-xs text-ink-500">{hint}</span>}
      {error && <span className="mt-1 block text-xs text-danger-600">{error}</span>}
    </label>
  )
}

type Tone = 'neutral' | 'navy' | 'success' | 'warning' | 'danger' | 'copper' | 'info'
/** Status chip per the design system: quiet text with a 1px stroke and a small
 *  square swatch (when `dot`), never a filled pill. */
export function Badge({ tone = 'neutral', children, className, dot }: { tone?: Tone; children: ReactNode; className?: string; dot?: boolean }) {
  const tones = {
    neutral: 'text-ink-700 border-ink-200',
    navy: 'text-navy-800 border-navy-300 bg-navy-100',
    success: 'text-success-700 border-success-500/35',
    warning: 'text-warning-700 border-warning-500/35',
    danger: 'text-danger-700 border-danger-500/35',
    copper: 'text-warning-700 border-warning-500/35 bg-warning-50',
    info: 'text-info-700 border-info-500/35',
  }
  const swatch = { neutral: 'bg-ink-400', navy: 'bg-navy-800', success: 'bg-success-500', warning: 'bg-warning-500', danger: 'bg-danger-500', copper: 'bg-warning-500', info: 'bg-info-500' }
  return (
    <span className={clsx('inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border bg-white px-2 py-0.5 text-xs font-medium leading-5', tones[tone], className)}>
      {dot && <span className={clsx('size-1.5 rounded-[1px]', swatch[tone])} />}
      {children}
    </span>
  )
}

export function Card({ children, className, title, actions, padded = true, style }: { children: ReactNode; className?: string; title?: ReactNode; actions?: ReactNode; padded?: boolean; style?: React.CSSProperties }) {
  return (
    <section className={clsx('card', className)} style={style}>
      {(title || actions) && (
        <header className="flex items-center justify-between gap-4 border-b border-line-100 px-5 py-3.5">
          {typeof title === 'string' ? <h3 className="text-sm font-semibold text-ink-900">{title}</h3> : title}
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={padded ? 'p-5' : ''}>{children}</div>
    </section>
  )
}

const nf = new Intl.NumberFormat('sv-SE')

/** Animates a number from its previous value to the new one (600 ms, ease-out). */
export function useCountUp(target: number, duration = 600) {
  const [value, setValue] = useState(target)
  const from = useRef(target)
  useEffect(() => {
    const start = from.current
    if (start === target) return
    const t0 = performance.now()
    let raf = 0
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / duration)
      const eased = 1 - Math.pow(1 - p, 3)
      setValue(start + (target - start) * eased)
      if (p < 1) raf = requestAnimationFrame(tick)
      else from.current = target
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, duration])
  return value
}

function AnimatedNumber({ value, format }: { value: number; format?: (n: number) => string }) {
  const v = useCountUp(value)
  return <>{format ? format(v) : nf.format(Math.round(v))}</>
}

export function Stat({ label, value, sub, tone, format, delay = 0, to }: { label: string; value: ReactNode | number; sub?: ReactNode; tone?: 'navy' | 'default'; format?: (n: number) => string; delay?: number; to?: string }) {
  const inner = (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-500">{label}</div>
        {to && <ArrowUpRight className="size-3.5 shrink-0 text-ink-300 transition-all duration-[140ms] group-hover:text-navy-700 group-hover:translate-x-px group-hover:-translate-y-px" />}
      </div>
      <div className={clsx('mt-2 text-2xl font-semibold tabular-nums tracking-[-0.02em]', tone === 'navy' ? 'text-navy-800' : 'text-ink-900')}>
        {typeof value === 'number' ? <AnimatedNumber value={value} format={format} /> : value}
      </div>
      {sub && <div className="mt-1 text-xs text-ink-500">{sub}</div>}
    </>
  )
  const style = { animationDelay: `${delay}ms` }
  if (to) {
    return (
      <Link to={to} className="card-interactive group block p-4 animate-fade-up" style={style} title="Visa detaljer">
        {inner}
      </Link>
    )
  }
  return <div className="card animate-fade-up p-4" style={style}>{inner}</div>
}

export function Progress({ value, tone = 'navy', className }: { value: number; tone?: 'navy' | 'success' | 'warning' | 'danger'; className?: string }) {
  const tones = { navy: 'bg-navy-800', success: 'bg-success-500', warning: 'bg-warning-500', danger: 'bg-danger-500' }
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true))
    return () => cancelAnimationFrame(id)
  }, [])
  const pctv = Math.min(100, Math.max(0, value * 100))
  return (
    <div className={clsx('h-1.5 w-full overflow-hidden rounded-full bg-line-200', className)} role="progressbar" aria-valuenow={Math.round(pctv)} aria-valuemin={0} aria-valuemax={100}>
      <div className={clsx('h-full rounded-full transition-[width] duration-700 ease-(--ease-out)', tones[tone])} style={{ width: mounted ? `${pctv}%` : '0%' }} />
    </div>
  )
}

export function EmptyState({ icon, title, description, action }: { icon?: ReactNode; title: string; description?: string; action?: ReactNode }) {
  return (
    <div className="animate-fade-up flex flex-col items-center justify-center rounded-(--radius-card) border border-line-200 bg-white px-6 py-12 text-center">
      {icon && <div className="mb-3 text-ink-400">{icon}</div>}
      <h3 className="text-sm font-semibold text-ink-900">{title}</h3>
      {description && <p className="mt-1 max-w-sm text-sm text-ink-500">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

export function Spinner({ className }: { className?: string }) {
  return (
    <div className={clsx('flex items-center justify-center py-12 text-ink-400', className)}>
      <Loader2 className="size-5 animate-spin" />
    </div>
  )
}

export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null
  const msg = error instanceof Error ? error.message : String(error)
  return <div className="rounded-lg border border-danger-500/30 bg-danger-50 px-3 py-2 text-sm text-danger-600">{msg}</div>
}

export function Modal({ open, onClose, title, children, footer, wide }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])
  if (!open) return null
  return (
    <div className="animate-fade-in fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-900/40 p-4 pt-[8vh]" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={clsx('surface animate-scale-in w-full shadow-(--shadow-modal)', wide ? 'max-w-4xl' : 'max-w-lg')} role="dialog" aria-modal>
        <header className="flex items-center justify-between border-b border-line-100 px-6 py-4">
          <h2 className="text-base font-semibold">{title}</h2>
          <button onClick={onClose} className="rounded-md p-1 text-ink-500 hover:bg-navy-50 hover:text-ink-900" aria-label="Stäng"><X className="size-4" /></button>
        </header>
        <div className="px-6 py-5">{children}</div>
        {footer && <footer className="flex items-center justify-end gap-2 border-t border-line-100 px-6 py-4">{footer}</footer>}
      </div>
    </div>
  )
}

export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: { key: T; label: string; count?: number }[]; value: T; onChange: (k: T) => void }) {
  const ref = useRef<HTMLElement>(null)
  const [ind, setInd] = useState<{ left: number; width: number } | null>(null)
  useLayoutEffect(() => {
    const el = ref.current?.querySelector<HTMLElement>(`[data-key="${value}"]`)
    if (el) setInd({ left: el.offsetLeft, width: el.offsetWidth })
  }, [value, tabs])
  return (
    <nav ref={ref} className="relative flex gap-1 border-b border-line-200" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.key}
          data-key={t.key}
          role="tab"
          aria-selected={t.key === value}
          onClick={() => onChange(t.key)}
          className={clsx(
            'inline-flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium transition-colors duration-[140ms]',
            t.key === value ? 'text-navy-900 font-semibold' : 'text-ink-500 hover:text-ink-700',
          )}
        >
          {t.label}
          {t.count !== undefined && <span className={clsx('rounded-full px-1.5 py-0.5 text-[11px] tabular-nums transition-colors duration-[140ms]', t.key === value ? 'bg-navy-100 text-navy-800' : 'bg-line-100 text-ink-500')}>{t.count}</span>}
        </button>
      ))}
      {ind && <span className="absolute -bottom-px h-0.5 rounded-full bg-navy-800 transition-[left,width] duration-[220ms] ease-(--ease-standard)" style={{ left: ind.left, width: ind.width }} />}
    </nav>
  )
}

export function PageHeader({ title, subtitle, actions, crumbs }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; crumbs?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4 animate-fade-up">
      <div className="min-w-0">
        {crumbs && <div className="mb-1.5 text-xs text-ink-500">{crumbs}</div>}
        <h1 className="text-[clamp(1.4rem,2.2vw,1.9rem)] leading-tight">{title}</h1>
        {subtitle && <div className="mt-1 text-sm text-ink-500">{subtitle}</div>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}

export function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2.5 text-sm text-ink-700">
      <span
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={clsx('relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors', checked ? 'bg-navy-800' : 'bg-ink-300')}
      >
        <span className={clsx('inline-block size-4 rounded-full bg-white shadow transition-transform', checked ? 'translate-x-4.5' : 'translate-x-0.5')} />
      </span>
      {label}
    </label>
  )
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={clsx('skeleton', className)} aria-hidden />
}

export function TableSkeleton({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div className="p-3">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-4 border-b border-line-100 py-3 last:border-0" style={{ animationDelay: `${r * 40}ms` }}>
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} className={clsx('h-3.5', c === 0 ? 'w-40' : c === cols - 1 ? 'w-16' : 'w-24')} />
          ))}
        </div>
      ))}
    </div>
  )
}

export function CardSkeleton() {
  return (
    <div className="card p-5">
      <Skeleton className="h-5 w-20" />
      <Skeleton className="mt-4 h-4 w-3/4" />
      <Skeleton className="mt-2 h-3.5 w-1/2" />
      <div className="mt-6 grid grid-cols-3 gap-3">
        <Skeleton className="h-8" /><Skeleton className="h-8" /><Skeleton className="h-8" />
      </div>
    </div>
  )
}

/** Person avatar carrying the S&P mark – the brand's only glyph. */
export function Avatar({ size = 'md', className }: { size?: 'sm' | 'md' | 'lg'; className?: string }) {
  const sizes = { sm: 'size-7 p-1.5', md: 'size-8 p-1.5', lg: 'size-10 p-2' }
  return (
    <span className={clsx('inline-flex shrink-0 items-center justify-center rounded-full bg-white shadow-(--shadow-card)', sizes[size], className)}>
      <img src={mark} alt="" className="size-full object-contain" draggable={false} />
    </span>
  )
}

/** Small pulsing dot used next to data that refreshes automatically. */
export function LiveDot({ label = 'Uppdateras automatiskt' }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-ink-500" title={label}>
      <span className="size-1.5 rounded-full bg-success-500 animate-pulse-soft" />
      {label}
    </span>
  )
}

/** A number that leads somewhere – used inside tables and cards. */
export function DrillLink({ to, children, className, title }: { to: string; children: ReactNode; className?: string; title?: string }) {
  return (
    <Link
      to={to}
      title={title ?? 'Visa raderna bakom siffran'}
      onClick={(e) => e.stopPropagation()}
      className={clsx('rounded px-1 -mx-1 tabular-nums underline decoration-transparent underline-offset-2 transition-colors duration-[140ms] hover:bg-navy-50 hover:text-navy-800 hover:decoration-navy-300', className)}
    >
      {children}
    </Link>
  )
}

/** Filter chips per the design system's segmented "filter" style. */
export function FilterChips<T extends string>({ options, value, onChange }: { options: { key: T; label: string; count?: number }[]; value: T; onChange: (k: T) => void }) {
  return (
    <div className="flex flex-wrap gap-1" role="group">
      {options.map((o) => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          className={clsx(
            'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors duration-[140ms]',
            o.key === value ? 'border-navy-900 bg-navy-900 text-white' : 'border-line-200 bg-white text-ink-700 hover:border-navy-300',
          )}
        >
          {o.label}
          {o.count !== undefined && <span className={clsx('tabular-nums', o.key === value ? 'text-white/60' : 'text-ink-400')}>{o.count}</span>}
        </button>
      ))}
    </div>
  )
}
